import React, { useState, useEffect, useCallback } from "react";
import {
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
  TicketIcon,
  CurrencyDollarIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  getAppUsers,
  updateUser,
  createUser,
  getCompanies,
  getUserSubscriptionDetails,
} from "../api/users";
import debounce from "lodash/debounce";
import { useFormik } from "formik";
import * as Yup from "yup";

const AppUsers = () => {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [limit] = useState(10);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSubscriptionSidebarOpen, setIsSubscriptionSidebarOpen] =
    useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Create validation schema
  const createValidationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    isEmailVerified: Yup.boolean().required("Email verified is required"),
    isActive: Yup.boolean().required("Active is required"),
  });

  // Update validation schema
  const updateValidationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    isEmailVerified: Yup.boolean().required("Email verified is required"),
    isActive: Yup.boolean().required("Active is required"),
  });

  // Create formik instance
  const createFormik = useFormik({
    initialValues: {
      name: "",
      email: "",
      isEmailVerified: true,
      isActive: true,
    },
    validationSchema: createValidationSchema,
    onSubmit: async (values) => {
      try {
        await createUser(values);
        toast.success("User created successfully");
        setIsCreateModalOpen(false);
        fetchUsers();
      } catch (error) {
        toast.error(error.message || "Failed to create user");
      }
    },
  });

  // Update formik instance
  const updateFormik = useFormik({
    initialValues: {
      name: "",
      company: "",
      isEmailVerified: false,
      isActive: true,
    },
    validationSchema: updateValidationSchema,
    onSubmit: async (values) => {
      try {
        await updateUser(selectedUser._id, values);
        toast.success("User updated successfully");
        setIsEditModalOpen(false);
        fetchUsers();
      } catch (error) {
        toast.error(error.message || "Failed to update user");
      }
    },
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getAppUsers({
        page: currentPage,
        limit,
        search,
        sortBy,
        sortOrder,
      });

      setUsers(response.data || []);
      setTotalPages(response.pagination.totalPages);
      setTotalItems(response.pagination.total);
    } catch (error) {
      toast.error(error.message || "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      const response = await getCompanies();
      console.log(response);
      setCompanies(response.data || []);
    };
    fetchCompanies();
  }, []);
  // Create a debounced version of fetchUsers
  const debouncedFetchUsers = useCallback(
    debounce(() => {
      fetchUsers();
    }, 500),
    [currentPage, search, sortBy, sortOrder],
  );

  useEffect(() => {
    debouncedFetchUsers();
    // Cleanup function to cancel any pending debounced calls
    return () => {
      debouncedFetchUsers.cancel();
    };
  }, [currentPage, search, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <ChevronUpIcon className="h-4 w-4 inline-block ml-1" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 inline-block ml-1" />
    );
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    updateFormik.setValues({
      name: user?.name || "",
      company: user?.company?._id || "",
      isEmailVerified: user?.isEmailVerified || false,
      isActive: user?.isActive ?? true,
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (user) => {
    // TODO: Implement delete functionality
    toast.error("Delete functionality coming soon");
  };

  const handleSubscriptionDetails = async (user) => {
    try {
      setSubscriptionLoading(true);
      setSelectedUser(user);
      const response = await getUserSubscriptionDetails(user._id);
      setSubscriptionData(response.data);
      setIsSubscriptionSidebarOpen(true);
    } catch (error) {
      toast.error("Failed to fetch subscription details");
      console.error("Error fetching subscription details:", error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCreateClick = () => {
    createFormik.resetForm();
    setIsCreateModalOpen(true);
  };

  return (
    <div className="mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">App Users</h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCreateClick}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add User
          </button>
          <div className="relative">
            <input
              type="text"
              placeholder="Search users..."
              value={search}
              onChange={handleSearch}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm table-compact">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("name")}
                >
                  Name <SortIcon column="name" />
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("email")}
                >
                  Email <SortIcon column="email" />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("createdAt")}
                >
                  Created <SortIcon column="createdAt" />
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Verified
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Coupon
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-3 py-3 text-center text-sm text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-3 py-3 text-center text-sm text-gray-500"
                  >
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-medium text-gray-900">
                        {user.name}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                      {user.email}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                      {user?.company?.name ?? "—"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className="inline-flex items-center gap-1"
                        title={user?.company?.payment_country || "—"}
                      >
                        {user?.company?.payment_country === "US" && (
                          <span className="text-base" title="United States">
                            <img
                              src="https://upload.wikimedia.org/wikipedia/en/a/a4/Flag_of_the_United_States.svg"
                              className="w-4 h-4"
                              alt="United States"
                            />
                          </span>
                        )}
                        {user?.company?.payment_country === "IN" && (
                          <span className="text-base" title="India">
                            <img
                              src="https://upload.wikimedia.org/wikipedia/en/4/41/Flag_of_India.svg"
                              className="w-4 h-4"
                              alt="India"
                            />
                          </span>
                        )}
                        {(!user?.company?.payment_country ||
                          !["US", "IN"].includes(
                            user.company.payment_country,
                          )) && <span className="text-gray-400">—</span>}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`px-1.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                          user.isEmailVerified
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {user.isEmailVerified ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                      {!user?.discount?.length ? (
                        "—"
                      ) : (
                        <span className="px-1.5 py-0.5 inline-flex text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          <TicketIcon className="h-4 w-4 mr-1" />
                          {user?.discount?.join(", ")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`px-1.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                          user.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {user.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex justify-end space-x-1">
                        <button
                          onClick={() => handleSubscriptionDetails(user)}
                          disabled={subscriptionLoading}
                          className="inline-flex items-center p-1.5 border border-transparent rounded text-[#000] disabled:opacity-50 hover:bg-gray-100"
                          title="View Subscription Details"
                        >
                          <CurrencyDollarIcon className="h-4 w-4" />
                        </button>
                        <button
                          className="inline-flex items-center p-1.5 border border-transparent rounded text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                          title="Edit User"
                          disabled
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          className="inline-flex items-center p-1.5 border border-transparent rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                          title="Delete User"
                          disabled
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-medium">
                  {(currentPage - 1) * limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * limit, totalItems)}
                </span>{" "}
                of <span className="font-medium">{totalItems}</span> results
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Pagination"
              >
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === index + 1
                        ? "z-10 bg-primary border-primary text-white"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={() => setIsCreateModalOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                    Create User
                  </h3>
                  <form
                    onSubmit={createFormik.handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={createFormik.values.name}
                        onChange={createFormik.handleChange}
                        onBlur={createFormik.handleBlur}
                        className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                          createFormik.errors.name && createFormik.touched.name
                            ? "border-red-500"
                            : "border-gray-300"
                        } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                      />
                      {createFormik.errors.name &&
                        createFormik.touched.name && (
                          <p className="mt-1 text-sm text-red-600">
                            {createFormik.errors.name}
                          </p>
                        )}
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={createFormik.values.email}
                        onChange={createFormik.handleChange}
                        onBlur={createFormik.handleBlur}
                        className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                          createFormik.errors.email &&
                          createFormik.touched.email
                            ? "border-red-500"
                            : "border-gray-300"
                        } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                      />
                      {createFormik.errors.email &&
                        createFormik.touched.email && (
                          <p className="mt-1 text-sm text-red-600">
                            {createFormik.errors.email}
                          </p>
                        )}
                    </div>
                    {/* 
                    <div>
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Password
                      </label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={createFormik.values.password}
                        onChange={createFormik.handleChange}
                        onBlur={createFormik.handleBlur}
                        className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                          createFormik.errors.password &&
                          createFormik.touched.password
                            ? "border-red-500"
                            : "border-gray-300"
                        } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                      />
                      {createFormik.errors.password &&
                        createFormik.touched.password && (
                          <p className="mt-1 text-sm text-red-600">
                            {createFormik.errors.password}
                          </p>
                        )}
                    </div> */}

                    {/* <div>
                      <label
                        htmlFor="company"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Company
                      </label>
                      <select
                        id="company"
                        name="company"
                        value={createFormik.values.company}
                        onChange={createFormik.handleChange}
                        onBlur={createFormik.handleBlur}
                        className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                          createFormik.errors.company &&
                          createFormik.touched.company
                            ? "border-red-500"
                            : "border-gray-300"
                        } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                      >
                        <option value="">Select Company</option>
                        {companies.map((company) => (
                          <option key={company._id} value={company._id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isEmailVerified"
                        name="isEmailVerified"
                        checked={createFormik.values.isEmailVerified}
                        onChange={createFormik.handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <label
                        htmlFor="isEmailVerified"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Email Verified
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={createFormik.values.isActive}
                        onChange={createFormik.handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <label
                        htmlFor="isActive"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Active
                      </label>
                    </div> */}

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setIsCreateModalOpen(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={createFormik.isSubmitting}
                        className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        {createFormik.isSubmitting ? (
                          <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        ) : (
                          "Create"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                    Edit User
                  </h3>
                  <form
                    onSubmit={updateFormik.handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={updateFormik.values.name}
                        onChange={updateFormik.handleChange}
                        onBlur={updateFormik.handleBlur}
                        className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                          updateFormik.errors.name && updateFormik.touched.name
                            ? "border-red-500"
                            : "border-gray-300"
                        } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                      />
                      {updateFormik.errors.name &&
                        updateFormik.touched.name && (
                          <p className="mt-1 text-sm text-red-600">
                            {updateFormik.errors.name}
                          </p>
                        )}
                    </div>

                    <div>
                      <label
                        htmlFor="company"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Company
                      </label>
                      <select
                        id="company"
                        name="company"
                        value={updateFormik.values.company}
                        onChange={updateFormik.handleChange}
                        onBlur={updateFormik.handleBlur}
                        className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                          updateFormik.errors.company &&
                          updateFormik.touched.company
                            ? "border-red-500"
                            : "border-gray-300"
                        } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                      >
                        <option value="">Select Company</option>
                        {companies.map((company) => (
                          <option key={company._id} value={company._id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isEmailVerified"
                        name="isEmailVerified"
                        checked={updateFormik.values.isEmailVerified}
                        onChange={updateFormik.handleChange}
                        disabled={selectedUser?.isEmailVerified}
                        className={`h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary ${
                          selectedUser?.isEmailVerified
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                      />
                      <label
                        htmlFor="isEmailVerified"
                        className={`ml-2 block text-sm text-gray-900 ${
                          selectedUser?.isEmailVerified ? "opacity-50" : ""
                        }`}
                      >
                        Email Verified
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={updateFormik.values.isActive}
                        onChange={updateFormik.handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <label
                        htmlFor="isActive"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Active
                      </label>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setIsEditModalOpen(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={updateFormik.isSubmitting}
                        className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        {updateFormik.isSubmitting ? (
                          <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        ) : (
                          "Update"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Details Sidebar */}
      {isSubscriptionSidebarOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40"
            onClick={() => setIsSubscriptionSidebarOpen(false)}
          />

          {/* Sidebar */}
          <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Subscription Details - {selectedUser?.name}
                </h3>
                <button
                  onClick={() => setIsSubscriptionSidebarOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {subscriptionLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : subscriptionData ? (
                  <div className="space-y-6">
                    {/* Subscription Status */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        Subscription Status
                      </h4>
                      <div className="flex items-center space-x-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            subscriptionData.subscription_details
                              ?.has_subscription
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {subscriptionData.subscription_details
                            ?.has_subscription
                            ? "Active"
                            : "No Subscription"}
                        </span>
                        {subscriptionData.subscription_details
                          ?.subscription_status && (
                          <span className="text-sm text-gray-600">
                            Status:{" "}
                            {
                              subscriptionData.subscription_details
                                .subscription_status
                            }
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Plan Details */}
                    {subscriptionData.subscription_details?.plan_details && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Plan Details
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-sm text-gray-500">
                              Amount:
                            </span>
                            <p className="text-sm font-medium">
                              $
                              {(
                                subscriptionData.subscription_details
                                  .plan_details.amount / 100
                              ).toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">
                              Interval:
                            </span>
                            <p className="text-sm font-medium capitalize">
                              {
                                subscriptionData.subscription_details
                                  .plan_details.interval
                              }
                            </p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">
                              Quantity:
                            </span>
                            <p className="text-sm font-medium">
                              {
                                subscriptionData.subscription_details
                                  .plan_details.quantity
                              }
                            </p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">
                              Plan ID:
                            </span>
                            <p className="text-sm font-medium text-gray-600">
                              {
                                subscriptionData.subscription_details
                                  .plan_details.plan_id
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Product Details */}
                    {subscriptionData.subscription_details?.plan_details
                      ?.product_id && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Product Details
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-sm text-gray-500">
                              Product Name:
                            </span>
                            <p className="text-sm font-medium">
                              {
                                subscriptionData.subscription_details
                                  .plan_details.product_id.name
                              }
                            </p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">
                              Product ID:
                            </span>
                            <p className="text-sm font-medium text-gray-600">
                              {
                                subscriptionData.subscription_details
                                  .plan_details.product_id.id
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Discount Details */}
                    {subscriptionData.subscription_details?.discount_details
                      ?.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Discount Details
                        </h4>
                        {subscriptionData.subscription_details.discount_details.map(
                          (discount, index) => (
                            <div
                              key={index}
                              className="border-l-4 border-blue-400 pl-4 mb-3"
                            >
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <div>
                                  <span className="text-sm text-gray-500">
                                    Coupon:
                                  </span>
                                  <p className="text-sm font-medium">
                                    {discount.coupon_name}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500">
                                    Discount:
                                  </span>
                                  <p className="text-sm font-medium">
                                    {discount.percent_off
                                      ? `${discount.percent_off}% off`
                                      : `$${discount.amount_off} off`}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500">
                                    Duration:
                                  </span>
                                  <p className="text-sm font-medium capitalize">
                                    {discount.duration}
                                  </p>
                                </div>
                                {discount.duration_in_months && (
                                  <div>
                                    <span className="text-sm text-gray-500">
                                      Months:
                                    </span>
                                    <p className="text-sm font-medium">
                                      {discount.duration_in_months}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    )}

                    {/* Billing Info */}
                    {subscriptionData.subscription_details?.billing_info && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">
                          Billing Information
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-sm text-gray-500">
                              Subscription Item Period Start:
                            </span>
                            <p className="text-sm font-medium">
                              {subscriptionData.subscription_details
                                .billing_info
                                .subscription_item_period_start_date
                                ? new Date(
                                    subscriptionData.subscription_details
                                      .billing_info
                                      .subscription_item_period_start_date,
                                  ).toLocaleDateString()
                                : subscriptionData.subscription_details
                                      .billing_info
                                      .subscription_item_period_start
                                  ? new Date(
                                      subscriptionData.subscription_details
                                        .billing_info
                                        .subscription_item_period_start * 1000,
                                    ).toLocaleDateString()
                                  : "N/A"}
                            </p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">
                              Subscription Item Period End:
                            </span>
                            <p className="text-sm font-medium">
                              {subscriptionData.subscription_details
                                .billing_info.subscription_item_period_end_date
                                ? new Date(
                                    subscriptionData.subscription_details
                                      .billing_info
                                      .subscription_item_period_end_date,
                                  ).toLocaleDateString()
                                : subscriptionData.subscription_details
                                      .billing_info.subscription_item_period_end
                                  ? new Date(
                                      subscriptionData.subscription_details
                                        .billing_info
                                        .subscription_item_period_end * 1000,
                                    ).toLocaleDateString()
                                  : "N/A"}
                            </p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">
                              Status:
                            </span>
                            <p className="text-sm font-medium">
                              {subscriptionData.subscription_details
                                .billing_info.status || "N/A"}
                            </p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">
                              Cancel at Period End:
                            </span>
                            <p className="text-sm font-medium">
                              {subscriptionData.subscription_details
                                .billing_info.cancel_at_period_end
                                ? "Yes"
                                : "No"}
                            </p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">
                              Created:
                            </span>
                            <p className="text-sm font-medium">
                              {subscriptionData.subscription_details
                                .billing_info.created_date
                                ? new Date(
                                    subscriptionData.subscription_details
                                      .billing_info.created_date,
                                  ).toLocaleDateString()
                                : subscriptionData.subscription_details
                                      .billing_info.created
                                  ? new Date(
                                      subscriptionData.subscription_details
                                        .billing_info.created * 1000,
                                    ).toLocaleDateString()
                                  : "N/A"}
                            </p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">
                              Billing Cycle Anchor:
                            </span>
                            <p className="text-sm font-medium">
                              {subscriptionData.subscription_details
                                .billing_info.billing_cycle_anchor_date
                                ? new Date(
                                    subscriptionData.subscription_details
                                      .billing_info.billing_cycle_anchor_date,
                                  ).toLocaleDateString()
                                : subscriptionData.subscription_details
                                      .billing_info.billing_cycle_anchor
                                  ? new Date(
                                      subscriptionData.subscription_details
                                        .billing_info.billing_cycle_anchor *
                                        1000,
                                    ).toLocaleDateString()
                                  : "N/A"}
                            </p>
                          </div>
                          {subscriptionData.subscription_details.billing_info
                            .canceled_at && (
                            <div>
                              <span className="text-sm text-gray-500">
                                Canceled At:
                              </span>
                              <p className="text-sm font-medium">
                                {new Date(
                                  subscriptionData.subscription_details
                                    .billing_info.canceled_at * 1000,
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Invoices Section */}
                    {subscriptionData.subscription_details?.invoices &&
                      subscriptionData.subscription_details?.invoices.length >
                        0 && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-900 mb-2">
                            Invoices
                          </h4>
                          <div className="space-y-3">
                            {subscriptionData.subscription_details?.invoices.map(
                              (invoice, index) => (
                                <div
                                  key={index}
                                  className="border border-gray-200 rounded-lg p-3"
                                >
                                  <div className="flex justify-between items-start mb-2">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        Invoice #{invoice.number}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {invoice.id}
                                      </p>
                                    </div>
                                    <span
                                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        invoice.status === "paid"
                                          ? "bg-green-100 text-green-800"
                                          : invoice.status === "open"
                                            ? "bg-yellow-100 text-yellow-800"
                                            : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {invoice.status}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                    <div>
                                      <span className="font-medium">
                                        Amount:
                                      </span>{" "}
                                      {invoice.amount_formatted}{" "}
                                      {invoice.currency.toUpperCase()}
                                    </div>
                                    <div>
                                      <span className="font-medium">
                                        Created:
                                      </span>{" "}
                                      {new Date(
                                        invoice.created_date,
                                      ).toLocaleDateString()}
                                    </div>
                                    <div>
                                      <span className="font-medium">
                                        Period Start:
                                      </span>{" "}
                                      {new Date(
                                        invoice.period_start_date,
                                      ).toLocaleDateString()}
                                    </div>
                                    <div>
                                      <span className="font-medium">
                                        Period End:
                                      </span>{" "}
                                      {new Date(
                                        invoice.period_end_date,
                                      ).toLocaleDateString()}
                                    </div>
                                  </div>
                                  <div className="mt-2 flex space-x-2">
                                    {invoice.pdf_url && (
                                      <a
                                        href={invoice.pdf_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-800"
                                      >
                                        View PDF
                                      </a>
                                    )}
                                    {invoice.hosted_url && (
                                      <a
                                        href={invoice.hosted_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-800"
                                      >
                                        View Online
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      No subscription data available
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AppUsers;
